/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2025, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  SEARCH_SCOPES, SearchParams, SearchResults, SearchScope,
} from './models/search.models';

/**
 * Search service that handles search and suggest requests to the backend API.
 */
@Injectable()
export class SearchService {
  private readonly searchEndpoint = 'search';

  /**
   * Constructor
   * @param http HttpClient for making HTTP requests
   */
  constructor(private http: HttpClient) {}

  /**
   * Perform a search query and return results from the server.
   *
   * @param searchParams The search parameters including query, userUnitId, from, and size
   * @returns Observable of search results
   */
  search(searchParams: SearchParams): Observable<SearchResults> {
    const {
      query, userUnitId, scope, from, size,
    } = searchParams;

    let params = new HttpParams()
      .set('query', query)
      .set('scope', scope || SEARCH_SCOPES.ALL)
      .set('from', (from || 0).toString())
      .set('size', (size || 20).toString());

    if (userUnitId) {
      params = params.set('userUnitId', userUnitId);
    }

    return this.http.get<SearchResults>(this.searchEndpoint, { params });
  }

  /**
   * Get search suggestions based on a prefix.
   *
   * @param prefix The user's typed-so-far query prefix
   * @param userUnitId
   * @param from
   * @param scope
   * @returns Observable of suggestion strings
   */
  suggest(prefix: string, userUnitId, from, scope: SearchScope = SEARCH_SCOPES.ALL): Observable<string[]> {
    let params = new HttpParams()
      .set('prefix', prefix)
      .set('from', from)
      .set('filter', '')
      .set('sort', '')
      .set('scope', scope);

    if (userUnitId) {
      params = params.set('userUnitId', userUnitId);
    }

    return this.http.get<string[]>(`${this.searchEndpoint}/suggest`, { params });
  }

  /**
   * Record search event for analytics.
   * This method can be enhanced to call analytics service.
   *
   * @param query The search query
   */
  recordSearchEvent(query: string): void {
    // TODO: Integrate with RecordEventService or analytics service
    // eslint-disable-next-line no-console
    console.log('Search submitted:', query);
  }
}
